import { authRoles } from "app/main/consts";
import moment from "moment";

import {
	LOCATION_ORIGIN,
	LOCATION_DESTINATION,
	CARRIER_ALL,
	TRUCK_ALL,
	CARRIER_ASSIGNED,
	TRUCK_ASSIGNED,
	CARRIER_TRUCKS,
	DRIVER_TRUCKS,
} from "../../search/config/searchFields";

const { actionOptions, ...autocomplete } = CARRIER_TRUCKS().autocomplete;
const CARRIER_TRUCKS_FILTERED = { ...CARRIER_TRUCKS(), autocomplete };

const createPanel = (classes, options) => {
	const entryPoint = options?.entryPoint;
	const role = options?.role;
	const topLevel = entryPoint === "topLevel";

	const isAdmin = authRoles.internalPowerUsers.includes(role);
	const isDispatch = authRoles.dispatcher.includes(role);
	const isCarrier = authRoles.externalUsers.includes(role);
	const isManager = authRoles.carrierManager.includes(role);
	const isInvestor = authRoles.carrierGeneric.includes(role);
	const isExtDispatcher = authRoles.carrierDispatcher.includes(role);
	const isDriver = authRoles.driver.includes(role);
	const isInternal = authRoles.internalUsers.includes(role);
	const isMainHandler = options.hasSubaccounts && (isCarrier || isManager || isExtDispatcher);

	let carrierSelector = isAdmin
		? CARRIER_ALL(true)
		: isDispatch || isMainHandler
		? CARRIER_ASSIGNED(isDispatch, true)
		: null;
	let truckSelector = isAdmin
		? TRUCK_ALL(true)
		: isDispatch || isMainHandler
		? TRUCK_ASSIGNED(isDispatch, true, isMainHandler)
		: isCarrier || isManager || isInvestor || isExtDispatcher
		? CARRIER_TRUCKS(isExtDispatcher, isInvestor, true)
		: isDriver
		? DRIVER_TRUCKS
		: null;

	return {
		form: {
			noErrorMessage: true,
			classes: {},
		},
		items: [
			{
				type: "group",
				group: {
					classes: {
						root: "flex-col md:flex-row",
						direction: undefined,
					},
				},
				content: {
					items: [
						...(topLevel ? [carrierSelector, truckSelector] : []),
						LOCATION_ORIGIN,
						{
							key: "start_date",
							type: "picker",
							label: "Start date",
							translate: "",
							field: {
								noErrorMessage: true,
								classes: {},
								variant: undefined,
							},
							picker: {
								disablePast: true,
								type: "date",
								classes: {},
								maxDate: moment().add(7, "d"),
							},
						},
						{
							...LOCATION_DESTINATION,
							disabled: !!options?.cycle,
							key: "end_location",
							type: "autocomplete",
							label: "End location",
							translate: "",
							field: {
								noErrorMessage: true,
								classes: {},
								variant: undefined,
							},
							picker: {
								disablePast: true,
								type: "date",
								classes: {},
								maxDate: moment().add(7, "d"),
							},
						},
						{
							key: "end_date",
							type: "picker",
							label: "Deliver date",
							translate: "",
							field: {
								noErrorMessage: true,
								classes: {},
								variant: undefined,
							},
							picker: {
								disablePast: true,
								type: "date",
								classes: {},
								maxDate: moment().add(13, "d"),
							},
						},
						{
							type: "menu",
							icon: "tune",
							button: { color: "primary", classes: {} },
							content: {
								items: [
									{
										key: "max_duration",
										type: "slider",
										label: "Max Duration",
										translate: "",
										defaultValue: 8,
										slider: {
											type: "input",
											prefix: "",
											postfix: " days",
											step: 1,
											min: 1,
											max: 11,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "blacklisted_states_ids",
										type: "autocomplete",
										label: "Blacklisted States",
										translate: "",
										field: {
											noErrorMessage: false,
											classes: {},
											variant: undefined,
										},
										autocomplete: {
											provider: "smarthop",
											url: "api/consts/states",
											types: [],
											multiple: true,
											anyValue: false,
											preloadDefault: true,
											icon: "location",
											maxValues: 10,
										},
									},
									{
										key: "min_total_rpm",
										type: "slider",
										label: "Min Total RPM",
										translate: "",
										defaultValue: 1.8,
										slider: {
											type: "input",
											step: 0.05,
											prefix: "$",
											postfix: "/mi",
											min: 0,
											max: 5,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "min_total_payment",
										type: "slider",
										label: "Min Total Payment",
										translate: "",
										defaultValue: 4500,
										slider: {
											type: "input",
											step: 100,
											prefix: "$",
											postfix: "",
											min: 0,
											max: 10000,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "path_length",
										type: "slider",
										label: "Max Legs",
										translate: "",
										defaultValue: 4,
										slider: {
											type: "input",
											prefix: "",
											postfix: " legs",
											step: 1,
											min: 1,
											max: 5,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "miles_range",
										type: "slider",
										label: "Miles Range",
										translate: "",
										defaultValueMin: 2400,
										defaultValueMax: 3200,
										slider: {
											type: "range",
											postfix: " mi",
											min: 600,
											max: 4000,
											step: 300,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "min_volume",
										type: "slider",
										label: "Min Volume",
										translate: "",
										defaultValue: 5,
										slider: {
											type: "input",
											prefix: "",
											postfix: " loads/day",
											step: 1,
											min: 2,
											max: 50,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										type: "group",
										group: {
											classes: {
												root: "flex-col md:flex-row",
												direction: undefined,
											},
										},
										content: {
											items: [
												{
													key: "n_markets",
													type: "slider",
													label: "Top Destination Markets",
													translate: "",
													defaultValue: 40,
													slider: {
														type: "input",
														step: 1,
														prefix: "",
														postfix: "",
														min: 1,
														max: 70,
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
												/*{
													key: "end_market_profit",
													type: "slider",
													label: "Ending Market Profit",
													translate: "",
													defaultValue: null,
													rules: {
														disabled: {
															ifNotEmptyRef: "n_markets",
														},
													},
													slider: {
														type: "input",
														step: 100,
														prefix: "$",
														postfix: "",
														min: -2000,
														max: 2000,
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},*/
											],
										},
									},
									/*{
										key: "max_paths",
										type: "slider",
										label: "Max Paths",
										translate: "",
										defaultValue: 100,
										slider: {
											type: "input",
											prefix: "",
											postfix: "",
											step: 5,
											min: 5,
											max: 100,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},*/
									{
										key: "min_deadhead_miles",
										type: "slider",
										label: "Min Deadhead Miles",
										translate: "",
										defaultValue: 40,
										slider: {
											type: "input",
											step: 20,
											prefix: "",
											postfix: " mi",
											min: 0,
											max: 450,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "min_loaded_miles_per_leg",
										type: "slider",
										label: "Min Loaded Miles",
										translate: "",
										defaultValue: 250,
										slider: {
											type: "input",
											step: 25,
											prefix: "",
											postfix: " mi",
											min: 0,
											max: 3000,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},

									{
										key: "miles_per_day",
										type: "slider",
										label: "Avg Miles per Day",
										translate: "",
										defaultValue: 600,
										slider: {
											type: "input",
											step: 50,
											prefix: "",
											postfix: " mi",
											min: 100,
											max: 1000,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									/*									
									{
										key: "allowed_states",
										type: "autocomplete",
										label: "Allowed States",
										translate: "",
										field: {
											noErrorMessage: false,
											classes: {},
											variant: undefined,
										},
										autocomplete: {
											provider: "smarthop",
											url: "api/consts/states",
											types: [],
											multiple: true,
											anyValue: false,
											preloadDefault: true,
											icon: "location",
											maxValues: 10,
										},
									},
									{
										key: "min_profit_per_leg",
										type: "slider",
										label: "Min Profit per Leg",
										translate: "",
										defaultValue: -500,
										slider: {
											type: "input",
											step: 50,
											prefix: "$ ",
											postfix: "",
											min: -2000,
											max: 200,
											noErrorMessage: true,
											classes: {},
											variant: undefined,
										},
									},
									{
										key: "min_profit",
										type: "number",
										label: "Target Profit",
										translate: "",
										defaultValue: -10000,
									},*/
									{
										type: "group",
										group: {
											classes: {
												root: "flex-col md:flex-row",
												direction: undefined,
											},
										},
										content: {
											items: [
												{
													key: "n_complete_checkpoints",
													type: "slider",
													label: "Exploration",
													translate: "",
													defaultValue: 500,
													slider: {
														type: "input",
														step: 100,
														prefix: "",
														postfix: "",
														min: 100,
														max: 2000,
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
												{
													key: "stop_at",
													type: "slider",
													label: "Stop at",
													translate: "",
													defaultValue: 5,
													slider: {
														type: "input",
														step: 1,
														prefix: "",
														postfix: "",
														min: 1,
														max: 20,
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
											],
										},
									},
									{
										type: "group",
										group: {
											classes: {
												root: "flex-col md:flex-row",
												direction: undefined,
											},
										},
										content: {
											items: [
												{
													key: "sort_by",
													type: "select",
													label: "Sort by",
													defaultValue: "total_rpm",
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
													options: [
														{
															value: "balanced_profit",
															label: "Balanced Profit",
															selected: true,
															translate: "balanced_profit",
														},
														{
															value: "total_profit",
															label: "Total profit",
															translate: "total_profit",
														},
														{
															value: "total_risk",
															label: "Total Risk",
															translate: "total_risk",
														},
														{
															value: "total_payment",
															label: "Total Payment",
															translate: "total_payment",
														},
														{
															value: "total_rpm",
															label: "Total RPM",
															translate: "total_rpm",
														},
														{
															value: "total_miles",
															label: "Total Miles",
															translate: "total_miles",
														},
													],
												},
												{
													key: "checkpoint_score_key",
													type: "select",
													label: "Scoring Key",
													defaultValue: "total_payment",
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
													options: [
														{
															value: "total_profit",
															label: "Total profit",
															translate: "total_profit",
														},

														{
															value: "total_payment",
															label: "Total Payment",
															translate: "total_payment",
														},
													],
												},
											],
										},
									},
									{
										type: "group",
										group: {
											classes: {
												root: "flex-col md:flex-row",
												direction: undefined,
											},
										},
										content: {
											items: [
												/*{
													key: "analysis_method",
													type: "select",
													label: "Analysis",
													defaultValue: "standard",
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
													options: [
														{
															value: "standard",
															label: "Standard",
															selected: true,
															translate: "standard",
														},
														{
															value: "volume",
															label: "Volume",
															translate: "volume",
														},
														{
															value: "probability",
															label: "Probability",
															translate: "probability",
														},
													],
												},*/

												{
													key: "use_price",
													type: "checkbox",
													label: "Price Calibration",
													defaultValue: true,
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
												{
													key: "force_compose",
													type: "checkbox",
													label: "Force Reload",
													defaultValue: false,
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
												{
													key: "use_nearby_clusters",
													type: "checkbox",
													label: "Nearby Options",
													defaultValue: false,
													field: {
														noErrorMessage: true,
														classes: {},
														variant: undefined,
													},
												},
											],
										},
									},
								],
							},
						},
						{
							type: "action",
							action: "SUBMIT",
							label: "Generate",
							icon: undefined,
							button: {
								alwaysEnabled: true,
								classes: {
									root: classes.buttonNormal,
									disabled: classes.buttonDisabled,
								},
							},
						},
					],
				},
			},
			{
				type: "action",
				button: {
					classes: {
						root: "hidden",
					},
				},
				confirmation: {
					enable: !!options?.cycle,
					message:
						"You have selected the cycle option, which means your destination is the same as your origin. Do you want to continue?",
				},
			},
		],
	};
};

export default createPanel;
